// Do not allow cheats in production

import { action, asyncAction } from '@play-co/replicant';
import createActions from './utils/createActions';
import { DynamicABTestBucketID, DynamicABTestID } from '../ruleset/ab';
import { stateSchema } from '../State';
import {
  Pet,
  PetAttribute,
  PetEvolutionStage,
  PetSpecies,
  PetStatusEffect,
} from '../buruburu/pet';
import { Inventory } from '../buruburu/inventory';
import { Level } from '../buruburu/level';
import { Milestone, MilestoneEvolution } from '../buruburu/level/level.ruleset';
import { ChatbotFlexMessageArgs } from '../chatbot/flex/utils';
import { TemplateRendererAPI } from '@play-co/replicant/lib/core/ReplicantChatbot';
import { duration } from '../utils/duration';
import { PetStats } from '../buruburu/pet/stats';
import { Fate } from '../buruburu/fate';
import { Duo } from '../duo';
import { nextSpeciesWeights } from '../buruburu/pet/pet.ruleset';
import { assertNever } from '../utils';
import {
  ChatbotStaticMessageArgs,
  getChatbotStaticMessage,
  sendChatbotMessage,
} from '../chatbot/chatbotMessage';
import { getPetAssetKey, speciesAssets } from '../chatbot/flex/species';
import { getLocaleConfig } from '../creatives/text';
import { getRandomItemFromArray } from '../utils/random';

export default createActions(
  process.env.IS_DEVELOPMENT
    ? {
        cheats_receiveDuoInvitation: action(
          (state, args: { userId: string }, api) => {
            state.duo.invitations.received.push({
              timestamp: api.date.now(),
              playerId: args.userId,
              careItemId:
                api.math.random() > 0.5
                  ? 'i_vowsCoffee'
                  : 'i_ultimateVowsCoffee',
              status: 'pending',
              pet: { species: 'giraffe', age: 42, name: 'Cheater' },
              lastReminder: 0,
            });

            state.duo.lastReceivedInvitation = {
              timestamp: api.date.now(),
              playerId: args.userId,
              name: 'Cheater',
            };
          },
        ),

        cheats_receiveDuoResponse: action(
          (
            state,
            { accepted, userId }: { accepted: boolean; userId: string },
            api,
          ) => {
            const careItemId =
              api.math.random() > 0.5 ? 'i_vowsCoffee' : 'i_ultimateVowsCoffee';

            const { species } = Pet.getters(state).getCurrentEvolution();

            state.duo.invitations.sent.push({
              timestamp: api.date.now(),
              playerId: userId,
              careItemId,
              status: accepted ? 'accepted' : 'cancelled',
              message:
                'Lorem ipsum dolor sit amet, consectetur adipiscing elit.',
              lastReminder: 0,
              pet: { species, age: 42, name: 'Cheater' },
            });

            state.duo.lastResponse = {
              timestamp: api.date.now(),
              playerId: userId,
              accepted,
              name: 'Cheater',
            };

            if (accepted) {
              state.duo.debut = {
                rewardClaimed: false,
                species: Duo.api.getNewSpecies(
                  [nextSpeciesWeights['giraffe'], nextSpeciesWeights['whale']],
                  api.math.random,
                ),
                careItemId,
                inviterId: userId,
                inviteeId: state.id,
              };
            }
          },
        ),

        cheat_addXP: action(
          (
            state,
            args: { milestone: Milestone; petStage: MilestoneEvolution },
          ) => {
            Level.modifiers(state).incrementXp(args.milestone, args.petStage);
          },
        ),

        cheat_addCoins: action((state, args: { amount: number }) => {
          const value = Math.max(0, (state.inventory.points += args.amount));
          state.inventory.points = value;
        }),

        cheat_addGems: action((state, args: { amount: number }) => {
          const gems = state.inventory.premium;

          // The difference can be deducted from paid gems, if amount is negative
          if (args.amount < 0) {
            const diff = gems.free + args.amount;
            if (diff < 0) {
              gems.paid = Math.max(0, gems.paid + diff);
            }
          }

          // Add or subtract free gems
          gems.free = Math.max(0, gems.free + args.amount);
        }),

        cheat_addEnergy: action((state, args: { amount: number }, api) => {
          if (args.amount < 0) {
            // Prevent to consume more energy than already have, to avoid errors
            const consume = Math.min(state.inventory.energy, -args.amount);
            Inventory.modifiers(state).consumeEnergy(consume, api.date.now());
            return;
          }
          const value = Math.max(0, (state.inventory.energy += args.amount));

          state.inventory.energy = value;
        }),

        cheat_setAttribute: action(
          (state, args: { attribute: PetAttribute; amount: number }, api) => {
            const now = api.date.now();
            let value = Pet.getters(state).getAttributeValue(
              args.attribute,
              now,
            );

            value += args.amount;

            PetStats.modifiers.setAttribute(state, args.attribute, value, now);

            console.warn(`Attribute ${args.attribute} set to ${value}%`);
          },
        ),

        cheat_setEffect: action(
          (state, args: { effect: PetStatusEffect }, api) => {
            const now = api.date.now();
            const currentValue = Pet.getters(state).hasStatusEffect(
              args.effect,
              now,
            );

            Fate.api.resolveStatusEffect(
              state,
              args.effect,
              !currentValue,
              now,
            );

            Fate.api.update(state, now);

            console.warn(
              `Pet status effect ${args.effect} set to ${!currentValue}%`,
            );
          },
        ),

        cheat_setHadVisitor: action((state, _, api) => {
          const now = api.date.now();
          const hadVisitor = Pet.getters(state).hasStatusEffect(
            'hadVisitor',
            now,
          );
          Fate.api.resolveStatusEffect(state, 'hadVisitor', !hadVisitor, now);
          Fate.api.update(state, now);

          if (!hadVisitor) {
            const { name } = state.publicProfile ?? state.profile;
            state.visit.visited = {
              [state.id]: {
                name,
                count: 1,
                lastUpdatedAt: now - duration({ hours: 1 }),
              },
            };
          }
        }),

        cheat_setSpecies: action(
          (
            state,
            args: { stage: PetEvolutionStage; species: PetSpecies },
            api,
          ) => {
            const len = state.pet.evolutions.length;

            state.pet.evolutions[len - 1].species = args.species;
            state.pet.evolutions[len - 1].stage = args.stage;
          },
        ),

        cheat_setVisitKickedOut: action(async (state) => {
          if (state.visit.visitSession) {
            state.visit.visitSession.isKickedOut = true;
          }
        }),

        cheat_reset: action((state, args: void, api) => {
          // Out with previous state.
          Object.keys(state).forEach((key) => delete state[key]);

          // In with the default state.
          Object.assign(state, stateSchema.getDefault());

          // Nuke metadata
          api.nukeUserMetainfo();
        }),

        cheat_setClockOffset: action((state, args: { offset: number }, api) => {
          api.setClockOffset(args.offset);
        }),

        cheat_getClockOffset: action((state, args: void, api) => {
          return api.getClockOffset();
        }),

        cheats_assignABTestManually: action(
          (
            state,
            args: { testId: DynamicABTestID; bucketId: DynamicABTestBucketID },
            api,
          ) => {
            api.abTests.assign(args.testId, args.bucketId);
          },
        ),

        cheats_resetABTestManualAssignment: action(
          (state, args: { testId: DynamicABTestID }, api) => {
            if (api.abTests.getBucketID(args.testId)) {
              api.abTests.unassign(args.testId);
            }
          },
        ),
        cheats_receive_gift: action((state, args: { userId: string }, api) => {
          state.gifts.received[args.userId] = {
            energy: 1,
            sentAt: api.date.now(),
          };
        }),
        cheats_reset_gifts: action((state, _: void, api) => {
          state.gifts.sent = {};
        }),
        cheats_reset_invites: action((state, _: void, api) => {
          state.social.invites = {};
        }),
        cheat_send_chatbot_message: asyncAction(
          async (
            state,
            args: ChatbotFlexMessageArgs & {
              debugAssets: Record<string, string>;
            },
            api,
          ) => {
            // Default generic chatbot image
            let imageUrl = `static/generic`;

            // Default generic title
            let title = 'TEST';

            // If status is provided, use one of the available status images
            const status = (args.data as any).status;
            if (status) {
              const speciesList = [
                'ball',
                'cat',
                'crab',
                'dog',
                'giraffe',
                'hamster',
                'seaotter',
                'starball',
                'turtle',
                'whale',
              ];
              const species = getRandomItemFromArray(speciesList, Math.random);
              imageUrl = `static/${species}/${status}`;
              title = getLocaleConfig('en').status[status].text;
            }

            const message = await getChatbotStaticMessage({
              state,
              api,
              imageUrl,
              title,
              payload: args.payload,
            });

            void sendChatbotMessage(
              api,
              api.getUserID(),
              message,
              state.platform || 'messenger',
            );

            return { contents: message };

            // Send flex message as usual
            // const message = getChatbotFlexMessage(args);

            // api.chatbot.sendMessage(api.getUserID(), message);

            // // Fake template api here
            // const templateRendering: TemplateRendererAPI<any> = {
            //   getAssetPath: (x) => args.debugAssets[x as string],
            //   getSenderID: () => state.id,
            //   getReceiverID: () => state.id,
            //   getUserAssetUrl: (key: string) => key,
            // };

            // // Use face API render finale template
            // const finalTemplate = message.renderers.line({
            //   args: args.data,
            //   payload: args.payload,
            //   api: templateRendering,
            // });

            // // Return sent template back to client
            // return finalTemplate;
          },
        ),

        cheat_send_chatbot_static_message: asyncAction(
          async (
            state,
            args: Omit<ChatbotStaticMessageArgs, 'state' | 'api'>,
            api,
          ) => {
            const message = await getChatbotStaticMessage({
              ...args,
              state,
              api,
            });

            void sendChatbotMessage(
              api,
              message.forceReceiverId || state.id,
              message,
              message.forcePlatform || state.platform || 'messenger',
            );

            return { contents: message };
          },
        ),

        cheat_setNativeAppInstalled: action(
          (
            state,
            args: { platform: 'google' | 'apple'; isInstalled: boolean },
            api,
          ) => {
            const nativeLaunchTime = args.isInstalled ? api.date.now() : 0;
            switch (args.platform) {
              case 'apple':
                state.native.appleLastLaunched = nativeLaunchTime;
                return;
              case 'google':
                state.native.googleLastLaunched = nativeLaunchTime;
                return;
              default:
                assertNever(args.platform);
            }
          },
        ),
      }
    : null,
);
